import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// ─── Billing & Plan -- local, per-browser UI-preview state only ──────────────
// Pricing/discount/mode config moved server-side in migration_v134/v135 (billing_config_defaults
// + org_billing_overrides, server/src/routes/billing.ts) -- Billing.tsx fetches that over
// GET /api/billing/effective instead of reading it from here. What's left in this context is
// genuinely local: which tier the CURRENT VIEWER is previewing on the comparison cards, and which
// billing cycle they're looking at -- neither is real subscription state (this app has no
// Stripe/payment processing), so there's nothing to lose keeping these client-side only.

export type PlanTierId = 'standard' | 'pro' | 'enterprise';
export type BillingMode = 'centralized' | 'per_facility';
export type BillingCycle = 'monthly' | 'yearly';

export interface TierPricing {
  basePricePerFacilityMonth: number;
  seatsIncludedPerFacility: number;
  pricePerExtraSeatMonth: number;
}

// Shape returned by GET /api/billing/effective and GET/PUT /api/billing/defaults|orgs/:id --
// kept here since Billing.tsx/Settings.tsx both need it and this was already the shared home for
// billing types before the server-backed move.
export interface EffectiveBillingConfig {
  tiers: Record<PlanTierId, TierPricing>;
  volumeDiscount10to20: number;
  volumeDiscount20plus: number;
  annualDiscount: number;
  billingMode: BillingMode;
  isOverride?: boolean;
  activeDiscountCode?: { code: string; discountType: 'month' | 'year'; discountPercent: number } | null;
  // The org's real total facility count, from the server (facilities.ts's GET /facilities is
  // deliberately role-scoped -- just 1 for a manager -- so it can't be reused for this; see the
  // comment on getOrgEffective in server/src/routes/billing.ts).
  orgFacilityCount?: number;
}

// Volume discount bracket lookup -- non-overlapping bands: 1-10 facilities = 0% (fixed, not
// configurable), 11-20 = volumeDiscount10to20, 21+ = volumeDiscount20plus.
export function volumeDiscountFor(
  facilityCount: number,
  cfg: { volumeDiscount10to20: number; volumeDiscount20plus: number },
): number {
  if (facilityCount >= 21) return cfg.volumeDiscount20plus;
  if (facilityCount >= 11) return cfg.volumeDiscount10to20;
  return 0;
}

interface PreviewState {
  selectedTier: PlanTierId;
  billingCycle: BillingCycle;
}

const DEFAULT_PREVIEW: PreviewState = {
  selectedTier: 'pro',
  billingCycle: 'monthly',
};

const STORAGE_KEY = 'proqure_billing_prototype_v1';

function loadStored(): PreviewState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREVIEW;
    const parsed = JSON.parse(raw);
    return {
      selectedTier: parsed.selectedTier ?? DEFAULT_PREVIEW.selectedTier,
      billingCycle: parsed.billingCycle ?? DEFAULT_PREVIEW.billingCycle,
    };
  } catch {
    return DEFAULT_PREVIEW;
  }
}

interface BillingConfigContextValue extends PreviewState {
  setSelectedTier: (tier: PlanTierId) => void;
  setBillingCycle: (cycle: BillingCycle) => void;
}

const BillingConfigContext = createContext<BillingConfigContextValue | null>(null);

export function BillingConfigProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PreviewState>(loadStored);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* per-viewer convenience only -- ignore quota errors */ }
  }, [state]);

  const setSelectedTier = useCallback((tier: PlanTierId) => setState((s) => ({ ...s, selectedTier: tier })), []);
  const setBillingCycle = useCallback((cycle: BillingCycle) => setState((s) => ({ ...s, billingCycle: cycle })), []);

  return (
    <BillingConfigContext.Provider value={{ ...state, setSelectedTier, setBillingCycle }}>
      {children}
    </BillingConfigContext.Provider>
  );
}

export function useBillingConfig(): BillingConfigContextValue {
  const ctx = useContext(BillingConfigContext);
  if (!ctx) throw new Error('useBillingConfig must be used within BillingConfigProvider');
  return ctx;
}
